import HotelStatusIcon from 'components/HotelStatusIcon'
import { SecondarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import { CheckIcon } from 'components/ReusableComponents/Icons/Check'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import TryLoginDialog from 'components/TryLoginDialog'
import { PMSsWithSyncLoginSupport, userEmailsRestartPMS } from 'constants/index'
import { PMSnames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import useNote from '../../Common/useNote'
import { useNoteBatchLoader } from '../../Common/NoteBatchLoaderContext'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'
import internalOnlyApi from 'services/api/internalOnly'
import pmsApi from 'services/api/pms'

import { Cell, Row, Spacer } from '../../../../components/ReusableComponents/Table'
import Toggle from '../../../../components/ReusableComponents/Toggle/index'
import HotelIdCell from '../../Common/HotelIdCell'
import HotelNameCell from '../../Common/HotelNameCell'
import { ReactComponent as BugIcon } from './bug.svg'
import styles from './PMSBErow.module.scss'

const NumberCell = ({ children, ...props }) => (
  <Cell className={styles.numberCell} {...props}>
    {children}
  </Cell>
)

const SyncRow = ({
  subsidiaryId,
  displayId,
  brand,
  shortName,
  groupName,
  pms,
  pmsHealthyKind,
  isAllReportHealthy,
  pmsSyncTotalReportsReceivedInLast48Hours,
  index,
  handleSetDebugData,
  problems,
  isHavingProblems = true,
  subsidiaryStatus,
  nextStartDate,
  lastResult,
  pmsSyncLastInfoReceivedOn,
  isDisabled,
  isInProgress,
  csmUserFullName,
  rowStatus,
  statusExplanation
}) => {
  const login = useSelector(state => state.login)
  const { registerCallback, notesCache } = useNoteBatchLoader()

  const {
    note,
    setNote,
    isLoading: isLoadingNote,
    isSaving,
    error: noteError,
    loadNoteOnDemand,
    setNoteFromBatch
  } = useNote(subsidiaryId, 'healthCheck-pms', { batchLoad: true })

  // Register callback to receive batch-loaded notes
  useEffect(() => {
    const key = `${subsidiaryId}-healthCheck-pms`
    
    // Check if note is already in cache
    if (notesCache.has(key)) {
      setNoteFromBatch(notesCache.get(key))
    }

    // Register callback for future updates
    const unregister = registerCallback(subsidiaryId, 'healthCheck-pms', (data) => {
      setNoteFromBatch(data)
    })

    return unregister
  }, [subsidiaryId, registerCallback, notesCache, setNoteFromBatch])

  const canReEnablePMS =
    login.isInternalAdmin &&
    userEmailsRestartPMS.includes(login.currentUser.email) &&
    PMSsWithSyncLoginSupport.includes(pms)

  const isPMSSupportesLogin = PMSsWithSyncLoginSupport.includes(pms)

  const [reEnablingPmsSync, setReEnablingPmsSync] = useState(false)

  const [isTryLoginDialogVisible, setIsTryLoginDialogVisible] = useState(false)
  const [isTryingLogin, setIsTryingLogin] = useState(false)
  const [tryLoginResponse, setTryLoginResponse] = useState(null)
  const [isTryLoginSuccessful, setIsTryLoginSuccessful] = useState()
  const [isPMSSyncEnabled, setIsPMSSyncEnabled] = useState(!isDisabled)

  function tryLogin() {
    setIsTryLoginDialogVisible(true)
    setIsTryingLogin(true)
    const credentialString = null
    internalOnlyApi
      .tryLogin({ subsidiaryId, credentialString })
      .then(res => {
        const response = res.response

        setTryLoginResponse(response)
        setIsTryLoginSuccessful(response.isSuccess)
      })
      .catch(err => {
        setTryLoginResponse(err)
        setIsTryLoginSuccessful(false)
      })
      .finally(() => {
        setIsTryingLogin(false)
      })
  }

  function closeTryLoginDialog() {
    setIsTryLoginDialogVisible(false)
    setTryLoginResponse(null)
    setIsTryLoginSuccessful(null)
  }

  const { addToast } = useToasts()

  useEffect(() => {
    setIsPMSSyncEnabled(isDisabled ? false : true)
  }, [isDisabled])

  function setStopPMSSync(status) {
    if (canReEnablePMS) {
      setReEnablingPmsSync(true)

      addToast(status ? 'Starting PMS sync...' : 'Stopping PMS sync...', {
        appearance: 'info',
        autoDismiss: true
      })

      pmsApi
        .pmsToggle({ SubsidiaryId: subsidiaryId, Pms: pms, Status: status })
        .then(() => {
          addToast(status ? 'PMS sync started' : 'PMS sync stopped', {
            appearance: 'success',
            autoDismiss: true
          })
        })
        .catch(err => {
          addToast(err.toString(), { appearance: 'error' })
          setIsPMSSyncEnabled(status)
        })
        .finally(() => {
          setIsPMSSyncEnabled(status)
          setReEnablingPmsSync(false)
        })
    } else {
      addToast('You do not have permission to perform this action', {
        appearance: 'error'
      })
    }
  }

  function pmsSyncMessage(message) {
    if (message === 'PasswordWrong') {
      return 'Password is wrong'
    } else if (message === 'PasswordExpired') {
      return 'Password is expired'
    } else if (message === 'NotIntegrated') {
      return 'Never integrated'
    } else if (message === 'SyncStopped') {
      return 'Sync stopped'
    } else if (message === 'PartiallyWorking') {
      return 'Partially working'
    } else {
      return '-'
    }
  }
  return (
    <div
      className={`${styles.rowHolder} ${rowStatus ? styles[rowStatus] : ''}`}
      title={statusExplanation}
    >
      {isTryLoginDialogVisible && (
        <TryLoginDialog
          isTryLoginSuccessful={isTryLoginSuccessful}
          isTryingLogin={isTryingLogin}
          onClose={closeTryLoginDialog}
          response={tryLoginResponse}
          brand={brand}
          shortName={shortName}
        />
      )}
      <div className={styles.index}>{index}</div>
      <div className={styles.statusIcon}>
        <HotelStatusIcon status={subsidiaryStatus} />
      </div>
      <Row>
        <HotelIdCell displayId={displayId} />
        <HotelNameCell
          brand={brand}
          name={shortName}
          groupName={groupName}
          navigationControl={
            <Link to={`/s/${subsidiaryId}/hotelDetails`}>
              <button>Go to hotel details →</button>
            </Link>
          }
        />
        <Spacer />
        <Cell className={styles.textCell}>
          <span title={csmUserFullName}>
            {csmUserFullName
              ?.split(' ')
              .map(str => str[0])
              .join('.')}
          </span>
        </Cell>
        <Cell>
          <div className={styles.pmsStatus}>
            {isDisabled ? (
              <>
                <ReactTooltip className={styles.pmsHealthyMessage} />
                <span data-tip={pmsSyncMessage(pmsHealthyKind)}>
                  <CrossIcon fill="red" />
                </span>
              </>
            ) : isAllReportHealthy ? (
              <CheckIcon />
            ) : (
              <>
                <ReactTooltip className={styles.pmsHealthyMessage} />
                <span data-tip={pmsSyncMessage(pmsHealthyKind)}>
                  <CrossIcon />
                </span>
              </>
            )}
          </div>
        </Cell>
        <Cell className={styles.textCell}>
          {isInProgress
            ? 'Syncing now'
            : nextStartDate
            ? moment(nextStartDate).add(-1, 'day').format('MMM DD, YYYY')
            : '-'}
        </Cell>
        <NumberCell>{pmsSyncTotalReportsReceivedInLast48Hours}</NumberCell>
        <Cell className={styles.textCell}>{PMSnames[pms]}</Cell>
        <Cell className={styles.doubleCell}>
          <p>
            {lastResult ? (
              lastResult?.slice(0, 30)
            ) : isPMSSupportesLogin ? (
              <span style={{ position: 'relative', top: 3 }}>No credentials</span>
            ) : (
              ''
            )}
          </p>
          {pmsSyncLastInfoReceivedOn && (
            <p>
              {pmsSyncLastInfoReceivedOn
                ? moment.utc(pmsSyncLastInfoReceivedOn).local().format('MMM DD, h:MM A')
                : '-'}
            </p>
          )}
        </Cell>

        <Cell className={styles.bugButtonCell}>
          {canReEnablePMS && (
            <Toggle
              className={styles.groupToggle}
              label=""
              disabled={reEnablingPmsSync}
              checked={isPMSSyncEnabled}
              handleChange={e => setStopPMSSync(e.target.checked)}
            />
          )}
          {PMSsWithSyncLoginSupport.includes(pms) && (
            <SecondarySmallButton
              style={{ transform: 'scale(0.9)' }}
              onClick={tryLogin}
              //disabled={!lastResult}
            >
              Test login
            </SecondarySmallButton>
          )}
        </Cell>
        <Cell className={styles.bugButtonHolder}>
          <div>
            {problems && (
              <button onClick={() => handleSetDebugData(problems)}>
                <BugIcon fill={isHavingProblems ? 'red' : '#cbccce'} />
              </button>
            )}
          </div>
        </Cell>
        <Cell className={styles.noteCell}>
          <input
            type="text"
            value={note || ''}
            onChange={e => setNote(e.target.value)}
            onFocus={loadNoteOnDemand}
            placeholder=" "
            disabled={isLoadingNote}
          />
          {isSaving && <span className={styles.savingIndicator}>Saving...</span>}
          {noteError && <div className={styles.noteError}>{noteError}</div>}
        </Cell>
      </Row>
    </div>
  )
}

export default SyncRow
